import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// origin/main literals: apps/api/src/decision-modules/segment-margin-cleanup/
// segment-margin-cleanup.data-lake-source.ts
// (SEGMENT_MARGIN_CLEANUP_DATA_LAKE_CONTEXT_KIND,
// SEGMENT_MARGIN_CLEANUP_DATA_LAKE_SCHEMA_VERSION_V2).
//
// Confirmed live on tenant helix_retail 2026-08-08 (read-only): 2 APPROVED,
// ingest-anchored rows at _v2 (approvedAt 2026-07-29, asOf 2026-06-30) plus 3
// older rows at _v1 (asOf 2025-12-31). _v2 is registered because it carries the
// freshest approved rows — the same choice made for carrier and, in #4521, for
// productivity-lean-ai. The freshest _v2 artifact's evidenceRefs carry
// segment_margin_snapshot and gl_account_decomposition.
//
// KEY NAMING (worth stating, because the names differ by design): the artifact's
// internal evidence-ref class is `segment_margin_snapshot`, but the key every
// intent requires — and the key the module publishes from its own workspace
// posture when this snapshot loads
// (segment-margin-cleanup.data-lake-source.ts: `evidenceKeys:
// ['approved_segment_margin_snapshot']`) — is `approved_segment_margin_snapshot`.
// The contract names the key the module publishes, which is what the readiness
// instrument resolves against.
//
// DECLARATION SITE: this key lives in governedCapability.requiredEvidence and in
// every intent's requiredEvidenceKeys, but NOT in manifest.requiredState — the
// same shape #4521 recorded for outside-contractors, labor-arbitrage, and
// productivity-lean-ai. requiredState's nine keys (corporate_residual_*,
// support_spend_*, segment_margin_floor_*, and the three hyphenated snapshot
// registers) are a separate attested/governed substrate; none is resolved through
// an AskHelixMemoryArtifact contextKind, so none is registered here.
//
// CADENCE (deliberately stricter than the module asks): the module's own
// requiredEvidence declares `maxAgeHours: 2160` — 90 days — with staleBehavior
// block. This substrate expresses freshness as a cadence, and no cadence equals
// 90 days (monthly is 45, quarterly 135). `monthly` is registered so the contract
// can never report this evidence fresher than the module itself would accept.
// At registration the live asOf is 39 days old: PRESENT, with roughly six days of
// headroom before it reads STALE without a refresh.
//
// SOURCE CLASS: the module's own requiredEvidence declares `manual-admin`, which
// is what is registered. Disclosed divergence: the live rows carry
// `csv-admin-upload`. The evidence instrument matches on contextKind and
// schemaVersion only and never filters on sourceClass, so this does not affect
// the measurement — but the contract states the module's declaration, and the
// divergence is recorded here rather than silently reconciled.
/**
 * THE evidence keys this module's intents require. Canonical home — the manifest,
 * the loaders and the workspace posture all IMPORT these; none retypes them
 * (Law 5).
 *
 * `marginFactDecomposition` is the SECOND authority the attribution intent needs
 * (#4754 amendment 9, finding 1). The approved formula requires both artifacts,
 * so an intent contract naming only the snapshot reported readiness from one of
 * two required records — the runtime then routed the advertised capability just
 * to refuse it with a named gap.
 */
export const SEGMENT_MARGIN_CLEANUP_EVIDENCE_KEYS = {
  segmentMarginSnapshot: 'approved_segment_margin_snapshot',
  marginFactDecomposition: 'approved_segment_margin_fact_decomposition',
} as const;

/**
 * THE display-evidence class the approved segment-margin snapshot's own display
 * refs carry (#4754 amendment 15, finding 2). Canonical home — the attribution
 * emission selects the approved display ref BY THIS CLASS, never by position,
 * because a governed _v2 snapshot can carry both this ref and the gl_account
 * decomposition ref in content-defined order (Law 5: imported, never retyped —
 * a divergent spelling would not fail loudly, it would silently match nothing
 * and the figure would fall back to the no-display-lineage disclosure path).
 * The spelling is the one live governed artifacts already carry.
 */
export const SEGMENT_MARGIN_CLEANUP_EVIDENCE_CLASSES = {
  segmentMarginSnapshot: 'segment-margin-snapshot',
} as const;

/**
 * THE context kinds this module's governed artifacts are stored under. Canonical
 * home — the approved-source registry, the approved formulas and the module's own
 * loaders all IMPORT these; none retypes them (#4754 amendment 8, finding 2,
 * Law 5). A context kind is the identity a governed read matches on, so a
 * divergent spelling does not fail loudly — it silently matches nothing.
 */
// Frozen because this constant is a governed formula authority: pvm_decomposition_v2
// binds it through the approved-formula origin fence, and `as const` is erased at
// compile time, so without the freeze a property could be rewritten after
// registration while the formula holds the string captured at initialization. The
// `as const` stays inside the freeze so the literal type is preserved rather than
// widened to string. Same Object.freeze posture the module-evidence factories and
// COMMERCIAL_EFFECTIVENESS_CONTEXT_KIND_BY_EVIDENCE_KEY already use in this folder.
export const SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS = Object.freeze({
  governedSnapshot: 'decision_agent:segment_margin_cleanup:governed_snapshot',
  marginFactDecomposition:
    'decision_agent:segment_margin_cleanup:margin_fact_decomposition',
} as const);

export const SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS = {
  governedSnapshotV2: 'segment_margin_cleanup_governed_snapshot_v2',
  marginFactV1: 'segment_margin_cleanup_margin_fact_v1',
} as const;

const NO_SEGMENT_MARGIN_CLEANUP_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

const segmentMarginSnapshotEntry = defineModuleEvidenceEntry(
  SEGMENT_MARGIN_CLEANUP_EVIDENCE_KEYS.segmentMarginSnapshot,
  {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
    contextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.governedSnapshot,
    schemaVersion: SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS.governedSnapshotV2,
    bindingPlaceholders: NO_SEGMENT_MARGIN_CLEANUP_BINDING_PLACEHOLDERS,
    domain: 'custom',
    freshnessCadence: 'monthly',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description:
      'Approved segment margin snapshot published by the governed segment-margin-cleanup ' +
      'data-lake source; carries segment margin with its gl-account decomposition, and backs ' +
      'every segment cleanup, mix-drift, and margin-floor intent.',
  },
);

/**
 * The SECOND artifact-backed authority (#4754 amendment 10). Registered in
 * `entries`, not merely in the keyspace, because the readiness and admission
 * tooling deliberately enumerates `contract.entries` — `reportEvidenceContracts`
 * in the CIO admission-catalog instrument, and the governed semantic graph, both
 * walk entries and never `keySpaces.evidenceKeys`. Declaring the key without an
 * entry left the modeled margin-fact artifact and its ingest contract invisible
 * to every one of those surfaces while runtime attribution required it.
 *
 * MODELED, and it says so: the approved formula admits this authority as its own
 * source class and the disclosure rides all the way to the answer. `requiredFor`
 * names `ask_helix` only — this artifact backs the attribution intent, not the
 * monitoring / scenario / decision-pack paths the snapshot backs, so claiming
 * those would overstate what it is required for.
 */
const segmentMarginFactDecompositionEntry = defineModuleEvidenceEntry(
  SEGMENT_MARGIN_CLEANUP_EVIDENCE_KEYS.marginFactDecomposition,
  {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.modeled,
    contextKind: SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS.marginFactDecomposition,
    schemaVersion: SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS.marginFactV1,
    bindingPlaceholders: NO_SEGMENT_MARGIN_CLEANUP_BINDING_PLACEHOLDERS,
    domain: 'custom',
    freshnessCadence: 'monthly',
    requiredFor: ['ask_helix'],
    missingBehavior: 'block',
    description:
      'Approved MODELED customer x SKU x channel x segment margin-fact rows published by the ' +
      'governed segment-margin-cleanup margin-fact source; the channel decomposition the ' +
      'margin attribution intent reconciles per segment to the approved segment P&L to the ' +
      'cent. Labeled modeled at every layer, never certified, and never summed with a ' +
      'connector-derived claim.',
  },
);

export const SEGMENT_MARGIN_CLEANUP_MODULE_EVIDENCE_CONTRACT = defineModuleEvidenceContract(
  'segment-margin-cleanup',
  {
    segment_margin_snapshot: segmentMarginSnapshotEntry,
    margin_fact_decomposition: segmentMarginFactDecompositionEntry,
  },
  {
    evidenceKeys: Object.values(SEGMENT_MARGIN_CLEANUP_EVIDENCE_KEYS),
    // Both classes this module's governed artifacts actually carry. Listing only
    // manual-admin described a one-authority module.
    sourceClasses: [
      DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
      DECISION_AGENT_SOURCE_CLASSES.modeled,
    ],
    contextKinds: Object.values(SEGMENT_MARGIN_CLEANUP_CONTEXT_KINDS),
    schemaVersions: Object.values(SEGMENT_MARGIN_CLEANUP_SCHEMA_VERSIONS),
    stateKeys: Object.values(SEGMENT_MARGIN_CLEANUP_EVIDENCE_KEYS),
    bindingPlaceholders: NO_SEGMENT_MARGIN_CLEANUP_BINDING_PLACEHOLDERS,
  },
);

export type SegmentMarginCleanupModuleEvidenceContract =
  typeof SEGMENT_MARGIN_CLEANUP_MODULE_EVIDENCE_CONTRACT;
